import React from 'react';
import Icon from '../../../components/AppIcon';

const RegistrationStatusTracker = ({ currentStatus, onStatusChange }) => {
  const statuses = [
    {
      id: 'draft',
      label: 'Draft',
      description: 'Initial setup',
      icon: 'FileEdit'
    },
    {
      id: 'submitted',
      label: 'Submitted',
      description: 'Under review',
      icon: 'Send'
    },
    {
      id: 'under-review',
      label: 'Under Review',
      description: 'Being evaluated',
      icon: 'Search'
    },
    {
      id: 'approved',
      label: 'Approved',
      description: 'Registration complete',
      icon: 'CheckCircle2'
    }
  ];

  const rejectedStatus = {
    id: 'rejected',
    label: 'Rejected',
    description: 'Needs revision',
    icon: 'XCircle'
  };

  const getStatusIndex = (status) => {
    return statuses?.findIndex(s => s?.id === status);
  };

  const currentIndex = getStatusIndex(currentStatus);
  const isRejected = currentStatus === 'rejected';

  const getStepStatus = (index) => {
    if (isRejected) return 'upcoming';
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'upcoming';
  };

  return (
    <div className="space-y-24">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground font-heading mb-4">
          Registration Status
        </h3>
        <p className="text-sm text-muted-foreground">
          Track your registration progress
        </p>
      </div>
      <div className="bg-card border border-border rounded-lg p-16 md:p-24">
        <div className="space-y-16">
          {statuses?.map((status, index) => {
            const stepStatus = getStepStatus(index);
            const isActive = currentStatus === status?.id;

            return (
              <div key={status?.id} className="relative">
                <div className={`
                  flex items-start gap-12 p-12 rounded-lg transition-smooth
                  ${isActive ? 'bg-primary/10 border-2 border-primary' : 'border-2 border-transparent'}
                `}>
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                    transition-smooth
                    ${stepStatus === 'completed' ? 'bg-success text-success-foreground' : ''}
                    ${stepStatus === 'current' ? 'bg-primary text-primary-foreground' : ''}
                    ${stepStatus === 'upcoming' ? 'bg-muted text-muted-foreground' : ''}
                  `}>
                    <Icon name={status?.icon} size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`
                        text-sm font-semibold transition-smooth
                        ${isActive ? 'text-primary' : 'text-foreground'}
                      `}>
                        {status?.label}
                      </h4>
                      {stepStatus === 'completed' && (
                        <Icon name="Check" size={16} className="text-success" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground caption">
                      {status?.description}
                    </p>
                  </div>
                </div>
                {index < statuses?.length - 1 && (
                  <div className="ml-5 h-8 w-0.5 bg-border my-4" />
                )}
              </div>
            );
          })}

          {isRejected && (
            <>
              <div className="ml-5 h-8 w-0.5 bg-border my-4" />
              <div className="bg-error/10 border-2 border-error rounded-lg p-12">
                <div className="flex items-start gap-12">
                  <div className="w-10 h-10 rounded-full bg-error text-error-foreground flex items-center justify-center flex-shrink-0">
                    <Icon name={rejectedStatus?.icon} size={18} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-error mb-4">
                      {rejectedStatus?.label}
                    </h4>
                    <p className="text-xs text-foreground caption">
                      {rejectedStatus?.description}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-24 pt-24 border-t border-border">
          <div className="flex items-center justify-between text-xs caption">
            <span className="text-muted-foreground">Last updated</span>
            <span className="text-foreground font-medium">
              {new Date()?.toLocaleDateString()} at {new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>
      {currentStatus === 'approved' && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-16">
          <div className="flex items-start gap-12">
            <Icon name="PartyPopper" size={20} className="text-success mt-2" />
            <div>
              <h4 className="text-sm font-semibold text-success mb-4">
                Registration Approved!
              </h4>
              <p className="text-xs text-foreground">
                Your partner registration has been successfully approved. You can now proceed to portfolio mapping and enablement.
              </p>
            </div>
          </div>
        </div>
      )}
      {currentStatus === 'rejected' && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-16">
          <div className="flex items-start gap-12">
            <Icon name="AlertCircle" size={20} className="text-error mt-2" />
            <div>
              <h4 className="text-sm font-semibold text-error mb-4">
                Registration Requires Revision
              </h4>
              <p className="text-xs text-foreground mb-12">
                Please review the feedback and update the required information:
              </p>
              <ul className="space-y-4 text-xs text-foreground">
                <li className="flex items-start gap-8">
                  <Icon name="Dot" size={16} className="flex-shrink-0 mt-1" />
                  <span>VAT Certificate document quality needs improvement</span>
                </li>
                <li className="flex items-start gap-8">
                  <Icon name="Dot" size={16} className="flex-shrink-0 mt-1" />
                  <span>Additional technical contact required</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationStatusTracker;